'use client';

import { cn } from '@/lib/utils';
import { Check, Copy, Shuffle } from 'lucide-react';
import { useEffect, useState } from 'react';

function hslToHex(h: number, s: number, l: number) {
  const a = (s * Math.min(l, 1 - l)) / 1;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function generatePalette(): string[] {
  const base = Math.floor(Math.random() * 360);
  const offsets = [0, 32, -28, 210, 180];
  return offsets.map((offset, i) => {
    const hue = (base + offset + 360) % 360;
    const saturation = 0.55 + (i % 2) * 0.15;
    const lightness = 0.4 + i * 0.08;
    return hslToHex(hue, saturation, Math.min(lightness, 0.85));
  });
}

function hexToRgb(hex: string) {
  const clean = hex.replace('#', '');
  const bigint = Number.parseInt(clean.length === 3 ? clean.replace(/(.)/g, '$1$1') : clean, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }) {
  const [rs, gs, bs] = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function contrastRatio(hexA: string, hexB: string) {
  const lumA = relativeLuminance(hexToRgb(hexA));
  const lumB = relativeLuminance(hexToRgb(hexB));
  const [lighter, darker] = lumA > lumB ? [lumA, lumB] : [lumB, lumA];
  return (lighter + 0.05) / (darker + 0.05);
}

export function ColorPaletteGenerator() {
  // Fixed default on first render (server + client must match); randomized
  // client-side only, after mount, to avoid a Math.random() hydration mismatch.
  const [palette, setPalette] = useState<string[]>([
    '#ff7f0f',
    '#f06005',
    '#ffc071',
    '#1a1006',
    '#fff8ed',
  ]);
  const [copied, setCopied] = useState<string | null>(null);
  const [fg, setFg] = useState('#1a1006');
  const [bg, setBg] = useState('#ffffff');

  useEffect(() => {
    setPalette(generatePalette());
  }, []);

  const ratio = contrastRatio(fg, bg);

  function copy(hex: string) {
    navigator.clipboard?.writeText(hex).then(() => {
      setCopied(hex);
      setTimeout(() => setCopied(null), 1200);
    });
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Palette generator</h3>
          <button
            type="button"
            onClick={() => setPalette(generatePalette())}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            <Shuffle className="size-3.5" /> Shuffle
          </button>
        </div>
        <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
          {palette.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => copy(hex)}
              className="group flex flex-col overflow-hidden rounded-xl border border-border"
            >
              <span className="block h-20 w-full sm:h-24" style={{ backgroundColor: hex }} />
              <span className="flex items-center justify-center gap-1 bg-card py-2 text-[11px] font-medium text-foreground">
                {copied === hex ? (
                  <Check className="size-3 text-primary" />
                ) : (
                  <Copy className="size-3 text-muted-foreground group-hover:text-foreground" />
                )}
                {hex}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold text-foreground">Contrast checker</h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <ColorInput label="Text color" name="text-color" value={fg} onChange={setFg} />
          <ColorInput
            label="Background color"
            name="background-color"
            value={bg}
            onChange={setBg}
          />
        </div>

        <div
          className="mt-4 flex items-center justify-center rounded-xl border border-border py-8 text-lg font-semibold"
          style={{ backgroundColor: bg, color: fg }}
        >
          Sample text
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-2xl font-bold text-foreground">{ratio.toFixed(2)}:1</span>
          <Badge pass={ratio >= 4.5} label="AA text" />
          <Badge pass={ratio >= 3} label="AA large text" />
          <Badge pass={ratio >= 7} label="AAA text" />
        </div>
      </div>
    </div>
  );
}

function ColorInput({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="mt-1 flex items-center gap-2 rounded-lg border border-input bg-background px-2">
        <input
          type="color"
          name={`${name}-picker`}
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="size-7 shrink-0 cursor-pointer rounded"
        />
        <input
          type="text"
          name={`${name}-hex`}
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full bg-transparent text-sm text-foreground outline-none"
        />
      </div>
    </label>
  );
}

function Badge({ pass, label }: { pass: boolean; label: string }) {
  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-1 text-xs font-medium',
        pass ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive',
      )}
    >
      {pass ? '✓' : '✕'} {label}
    </span>
  );
}
